/**
 * A website enquiry's life.
 *
 * Pure, and shared in shape with the backend's `contact.ts`. Three states,
 * because the only question this console has to answer is "has a person dealt
 * with this" — anything finer would be a ticketing system, and the contact
 * page promises a person, not a ticket.
 */

export type ContactStatus = "new" | "answered" | "closed";
export type ContactSubject = "general" | "studios" | "schools" | "dataProtection" | "press";
export type ContactSegment = "studio" | "school" | "other";

export interface ContactSubmission {
  /** Written by the backend into the document, because the console reads collections as plain data. */
  id?: string;
  name: string;
  email: string;
  message: string;
  institution?: string;
  country?: string;
  subject: ContactSubject;
  segment: ContactSegment;
  locale: "en" | "de";
  status: ContactStatus;
  receivedAt: string;
  statusChangedAt?: string;
  statusChangedBy?: string;
}

export const CONTACT_STATUSES: readonly ContactStatus[] = ["new", "answered", "closed"];

export const STATUS_KEY: Record<ContactStatus, string> = {
  new: "enquiries.status.new",
  answered: "enquiries.status.answered",
  closed: "enquiries.status.closed",
};

export const STATUS_TONE: Record<ContactStatus, string> = {
  new: "text-cue-amber",
  answered: "text-cue-green",
  closed: "text-ink-muted",
};

export const SUBJECT_KEY: Record<ContactSubject, string> = {
  general: "enquiries.subject.general",
  studios: "enquiries.subject.studios",
  schools: "enquiries.subject.schools",
  dataProtection: "enquiries.subject.dataProtection",
  press: "enquiries.subject.press",
};

export const SEGMENT_KEY: Record<ContactSegment, string> = {
  studio: "enquiries.segment.studio",
  school: "enquiries.segment.school",
  other: "enquiries.segment.other",
};

/** The label on the button that moves a row INTO this state. */
export const ACTION_KEY: Record<ContactStatus, string> = {
  new: "enquiries.action.reopen",
  answered: "enquiries.action.answered",
  closed: "enquiries.action.close",
};

/**
 * A data-protection question has a clock on it — the contact page says so —
 * and it is the one kind of enquiry that must not wait behind the others.
 */
export function isUrgent(row: ContactSubmission): boolean {
  return row.subject === "dataProtection";
}

function rank(row: ContactSubmission): number {
  if (row.status !== "new") return 2;
  return isUrgent(row) ? 0 : 1;
}

/** Unanswered first, data-protection questions at the very top, newest first within each band. */
export function ordered(rows: readonly ContactSubmission[]): ContactSubmission[] {
  return [...rows].sort((a, b) => {
    const band = rank(a) - rank(b);
    if (band !== 0) return band;
    return b.receivedAt.localeCompare(a.receivedAt);
  });
}

export function countUnanswered(rows: readonly ContactSubmission[]): number {
  return rows.filter((row) => row.status === "new").length;
}

/** Where a row can go from where it is. Answered comes before closed: it is the usual next step. */
export function nextStatuses(status: ContactStatus): ContactStatus[] {
  switch (status) {
    case "new":
      return ["answered", "closed"];
    case "answered":
      return ["closed", "new"];
    case "closed":
      return ["new"];
  }
}

/**
 * A reply, started in the admin's own mail client.
 *
 * Deliberately not a form in the console: the promise is a person answering,
 * and a person answers from their own address, in a thread the sender can
 * reply to. The subject is in the sender's language, not the admin's.
 */
export function replyHref(row: ContactSubmission): string {
  const subject = row.locale === "de" ? "Re: Ihre Nachricht an Spontar" : "Re: your message to Spontar";
  return `mailto:${row.email}?subject=${encodeURIComponent(subject)}`;
}
